import { Add } from '@mui/icons-material'
import { Alert, Box, Button, Snackbar, TablePagination, Typography } from '@mui/material'
import axios from 'axios'
import { useEffect, useMemo, useState } from 'react'
import { useUser } from '../hooks'
import NotesDrawer, { type NoteItem } from '../components/notes/NotesDrawer'
import { ManufacturerSuppliersDrawer } from './ManufacturerSuppliersDrawer'
import { usePagination } from '../components/shared/usePagination'
import { useDrawerState } from '../components/shared/useDrawerState'
import { ConfirmDialog } from '../components/shared/ConfirmDialog'
import { EditManufacturerDrawer } from './EditManufacturerDrawer'
import { ManufacturerFilters } from './ManufacturerFilters'
import { ManufacturerModal } from './ManufacturerModal'
import { ManufacturerTable } from './ManufacturerTable'
import { useManufacturerFilters } from './useManufacturerFilters'
import '../components/styles/ListPage.css'
import type { EditContext, ManufacturerDraft, ManufacturerRow } from './ManufacturerList.types'
import { useManufacturerData } from './useManufacturerData'
import { useSearchParams } from 'react-router-dom'
import { cleanContractAliases } from './helpers'

export default function ManufacturerList() {
  const user = useUser()
  const { rows, setRows, loading, error: loadError, setError: setLoadError } = useManufacturerData()
  const [searchParams] = useSearchParams()

  // Filters
  const { filters, setFilters, filteredRows, manufacturerOptions } = useManufacturerFilters(rows)

  // Pagination
  const {
    page,
    rowsPerPage,
    pagedItems: pagedRows,
    totalCount,
    handlePageChange,
    handleRowsPerPageChange,
  } = usePagination(filteredRows, 25)

  // Expand/collapse state
  const [expandedById, setExpandedById] = useState<Record<number, boolean>>({})

  const effectiveExpandedById = useMemo(() => {
    if (!filters.alias.trim()) return expandedById
    return Object.fromEntries(pagedRows.map(r => [r.id, true]))
  }, [filters.alias, pagedRows, expandedById])

  const authorizedOemDrawer = useDrawerState()
  const editDrawer = useDrawerState()
  const addDrawer = useDrawerState()
  const notesDrawer = useDrawerState()

  // Notes state
  const [notesMap, setNotesMap] = useState<Partial<Record<number, NoteItem[]>>>({})
  const [activeManufacturerId, setActiveManufacturerId] = useState<number | null>(null)

  const activeNotes = useMemo(() => {
    if (activeManufacturerId === null) return []
    return notesMap[activeManufacturerId] ?? []
  }, [activeManufacturerId, notesMap])

  // Edit context
  const [editContext, setEditContext] = useState<EditContext | null>(null)

  // Set page title
  useEffect(() => {
    document.title = 'Manufacturers List | I-Quote'
  }, [])

  useEffect(() => {
    const mfr = searchParams.get('mfr')
    const mfrName = searchParams.get('mfrName')
    if (mfr) {
      setFilters(prev => ({ ...prev, oemCode: mfr }))
    } else if (mfrName) {
      setFilters(prev => ({ ...prev, manufacturer: mfrName }))
    }
  }, [searchParams])

  // Get manufacturer for edit drawer
  const selectedEditManufacturer = useMemo(() => {
    if (!editContext) return null
    return rows.find(r => r.id === editContext.manufacturerId) ?? null
  }, [rows, editContext])

  // Event handlers
  const openEditManufacturer = (manufacturerId: number) => {
    setEditContext({ kind: 'manufacturer', manufacturerId })
    editDrawer.open()
  }

  const openEditAlias = (manufacturerId: number, aliasIndex: number) => {
    setEditContext({ kind: 'alias', manufacturerId, aliasIndex })
    editDrawer.open()
  }

  const closeEditDrawer = () => {
    editDrawer.close()
    setEditContext(null)
  }

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  })

  const handleDeleteManufacturer = async () => {
    if (!editContext) return
    try {
      await axios.delete(`/manufacturer/${editContext.manufacturerId}`)
      setRows(prev => prev.filter(r => r.id !== editContext.manufacturerId))
      setDeleteConfirmOpen(false)
      closeEditDrawer()
      setSnackbar({ open: true, message: 'Manufacturer deleted.', severity: 'success' })
    } catch (err) {
      console.error('Failed to delete manufacturer', err)
      setDeleteConfirmOpen(false)
      setSnackbar({ open: true, message: 'Failed to delete manufacturer. Please try again.', severity: 'error' })
    }
  }

  const toggleExpand = (id: number) => {
    setExpandedById(prev => ({
      ...prev,
      [id]: !prev[id],
    }))
  }

  const handleUpdateManufacturer = async (draft: ManufacturerDraft) => {
    if (!editContext) return
    const ctx = editContext

    try {
      if (ctx.kind === 'alias') {
        const aliasId = rows.find(r => r.id === ctx.manufacturerId)?.aliasIds[ctx.aliasIndex]
        if (aliasId !== undefined) {
          await axios.put(`/manufacturer/${ctx.manufacturerId}/alias`, { aliasId, newAlias: draft.alias })
        }
      } else {
        await axios.put(`/manufacturer/${ctx.manufacturerId}`, {
          provider: draft.supplier,
          mfrCodeUpper: draft.mfrCodeUpper,
          url: draft.url,
          direct: draft.direct,
        })
        const before = rows.find(r => r.id === ctx.manufacturerId)?.contractAliases ?? []
        const after = cleanContractAliases(draft.contractAliases)
        const aliasesFor = (entries: typeof after, contractNumber: string) =>
          entries.filter(entry => entry.contractNumber === contractNumber).map(entry => entry.alias)
        const changedContracts = [...new Set([...before, ...after].map(entry => entry.contractNumber))].filter(
          contractNumber =>
            aliasesFor(before, contractNumber).sort().join('|') !== aliasesFor(after, contractNumber).sort().join('|'),
        )
        await Promise.all(
          changedContracts.map(contractNumber =>
            axios.put(`/manufacturer/${ctx.manufacturerId}/contract-alias`, {
              contractNumber,
              aliases: aliasesFor(after, contractNumber),
            }),
          ),
        )
      }
    } catch (err) {
      console.error('Failed to update manufacturer', err)
      setSnackbar({
        open: true,
        message:
          (axios.isAxiosError(err) ? err.response?.data?.message : null) ??
          'Failed to update manufacturer. Please try again.',
        severity: 'error',
      })
      return
    }

    setRows(prev =>
      prev
        .map(m => {
          if (m.id !== ctx.manufacturerId) return m

          const baseUpdated: ManufacturerRow = {
            ...m,
            supplier: draft.supplier,
            mfrCodeUpper: draft.mfrCodeUpper,
            url: draft.url,
            direct: draft.direct,
            contractAliases: cleanContractAliases(draft.contractAliases),
          }

          if (ctx.kind === 'alias') {
            const nextAliases = [...baseUpdated.aliases]
            nextAliases[ctx.aliasIndex] = draft.alias
            return { ...baseUpdated, aliases: nextAliases }
          }

          return baseUpdated
        })
        .sort((a, b) => a.supplier.localeCompare(b.supplier, undefined, { sensitivity: 'base' })),
    )

    closeEditDrawer()
  }

  const handleAddManufacturer = async (draft: ManufacturerDraft) => {
    try {
      const { data } = await axios.post<{ id: number }>('/manufacturer', {
        provider: draft.supplier.trim(),
        mfrCodeUpper: draft.mfrCodeUpper.trim(),
        url: draft.url.trim(),
        direct: draft.direct,
        alias: draft.alias.trim(),
        contractAliases: cleanContractAliases(draft.contractAliases),
      })

      const newRow: ManufacturerRow = {
        id: data.id,
        supplier: draft.supplier.trim(),
        aliases: draft.alias.trim() ? [draft.alias.trim()] : [],
        contractAliases: cleanContractAliases(draft.contractAliases),
        aliasIds: [],
        mfrCodeUpper: draft.mfrCodeUpper.trim(),
        url: draft.url.trim(),
      }

      setRows(prev =>
        [...prev, newRow].sort((a, b) => a.supplier.localeCompare(b.supplier, undefined, { sensitivity: 'base' })),
      )
      addDrawer.close()
    } catch (err) {
      console.error('Failed to create manufacturer', err)
      setSnackbar({
        open: true,
        message:
          (axios.isAxiosError(err) ? err.response?.data?.message : null) ??
          'Failed to add manufacturer. Please try again.',
        severity: 'error',
      })
    }
  }

  return (
    <div className="list-page">
      <ManufacturerFilters filters={filters} onFiltersChange={setFilters} manufacturerOptions={manufacturerOptions} />

      <div className="list-table-container">
        <div className="list-table-pagination-header">
          <div className="list-table-pagination-header-left">
            <Typography variant="h6">Manufacturers</Typography>
            <Button variant="contained" size="large" startIcon={<Add />} onClick={addDrawer.open}>
              Add New
            </Button>
          </div>

          <TablePagination
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
          />
        </div>

        <ManufacturerTable
          loading={loading}
          rows={pagedRows}
          expandedById={effectiveExpandedById}
          onToggleExpand={toggleExpand}
          onEditManufacturer={openEditManufacturer}
          onEditAlias={openEditAlias}
          onOpenAuthorizedOem={(manufacturer: ManufacturerRow) => {
            setActiveManufacturerId(manufacturer.id)
            authorizedOemDrawer.open()
          }}
          onOpenNotes={(rowId: number) => {
            setActiveManufacturerId(rowId)
            notesDrawer.open()
          }}
        />

        <Box display="flex" justifyContent="flex-end">
          <TablePagination
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
          />
        </Box>
      </div>

      {/* Drawers and Modals */}
      <NotesDrawer
        open={notesDrawer.isOpen}
        onClose={() => {
          notesDrawer.close()
          setActiveManufacturerId(null)
        }}
        header="Notes"
        notes={activeNotes}
        onAddNote={({ notes: noteText }) => {
          if (activeManufacturerId === null) return
          const today = new Date()
          const newNote: NoteItem = {
            id: crypto.randomUUID(),
            dateTime: `${String(today.getMonth() + 1).padStart(2, '0')}/${String(today.getDate()).padStart(
              2,
              '0',
            )}/${today.getFullYear()}`,
            user: user.displayName ?? 'Unknown',
            title: 'Update',
            notes: noteText,
          }
          setNotesMap(prev => ({
            ...prev,
            [activeManufacturerId]: [newNote, ...(prev[activeManufacturerId] ?? [])],
          }))
        }}
      />

      <ManufacturerSuppliersDrawer
        open={authorizedOemDrawer.isOpen}
        onClose={() => {
          authorizedOemDrawer.close()
          setActiveManufacturerId(null)
        }}
        manufacturerId={activeManufacturerId}
      />

      <EditManufacturerDrawer
        open={editDrawer.isOpen}
        onClose={closeEditDrawer}
        manufacturer={selectedEditManufacturer}
        context={editContext}
        onSave={handleUpdateManufacturer}
        onDelete={() => setDeleteConfirmOpen(true)}
      />

      <ConfirmDialog
        open={deleteConfirmOpen}
        title="Delete Manufacturer?"
        body="Are you sure you want to delete this manufacturer? This will also remove it from any linked suppliers."
        confirmLabel="DELETE"
        cancelLabel="CANCEL"
        onConfirm={handleDeleteManufacturer}
        onCancel={() => setDeleteConfirmOpen(false)}
      />

      <ManufacturerModal open={addDrawer.isOpen} onClose={addDrawer.close} onSave={handleAddManufacturer} />

      <Snackbar open={snackbar.open}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
      <Snackbar open={!!loadError} autoHideDuration={6000} onClose={() => setLoadError(null)}>
        <Alert severity="error" onClose={() => setLoadError(null)} variant="filled">
          Manufacturer data could not be loaded. Please try again or contact support if the problem persists.
        </Alert>
      </Snackbar>
    </div>
  )
}
